import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronRight, CheckCircle, XCircle, Clock, Award, BarChart3, User } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatTime12WithTz, getTimezoneAbbr } from "@/utils/timezone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// scheduled_time from /tutor/my-students is already in the tutor's local timezone —
// use formatTime12WithTz (raw 12h formatter, no UTC conversion) to avoid double-converting.
const tutorTzAbbr = getTimezoneAbbr();
const formatTime = (t) => formatTime12WithTz(t, tutorTzAbbr);

const TutorQuizResults = () => {
  const location = useLocation();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [quizData, setQuizData] = useState(null);
  const [loadingQuiz, setLoadingQuiz] = useState(false);
  const [expandedWeek, setExpandedWeek] = useState(null);

  const token = localStorage.getItem("access_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadStudents();
  }, []);

  // Check if URL has an enrollment ID
  useEffect(() => {
    const parts = location.pathname.split("/");
    const last = parts[parts.length - 1];
    if (last && last !== "quiz-results" && last.length > 10) {
      setSelectedEnrollment(last);
      loadQuizResults(last);
    }
  }, [location.pathname]);

  const loadStudents = async () => {
    try {
      const res = await axios.get(`${API}/tutor/my-students`, { headers });
      setStudents(res.data);
    } catch {
      toast.error("Failed to load students");
    } finally {
      setLoading(false);
    }
  };

  const loadQuizResults = async (enrollmentId) => {
    setLoadingQuiz(true);
    setExpandedWeek(null);
    try {
      const res = await axios.get(`${API}/tutor/student-quiz-results/${enrollmentId}`, { headers });
      setQuizData(res.data);
      setSelectedEnrollment(enrollmentId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load quiz results");
      setQuizData(null);
    } finally {
      setLoadingQuiz(false);
    }
  };

  const backToList = () => {
    setSelectedEnrollment(null);
    setQuizData(null);
    setExpandedWeek(null);
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return "—";
    try {
      return new Date(isoStr).toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit"
      });
    } catch { return isoStr; }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="spinner"></div></div>;

  // ---- Detail View: Show quiz results for selected student ----
  if (selectedEnrollment && quizData) {
    return (
      <div data-testid="quiz-results-detail">
        <button onClick={backToList} className="flex items-center gap-1 text-[#4A4A4A] hover:text-[#0A0A0A] text-sm mb-6" data-testid="back-to-students">
          <ArrowLeft className="w-4 h-4" /> Back to Students
        </button>

        {/* Student Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-[#A0D2F8] border-2 border-[#0A0A0A] flex items-center justify-center text-xl font-bold">
            {quizData.student_name?.charAt(0)}
          </div>
          <div>
            <h1 className="text-2xl font-bold" data-testid="quiz-student-name">{quizData.student_name}</h1>
            <p className="text-[#4A4A4A]">
              {quizData.language_flag} {quizData.course_name} &middot; Week {quizData.current_week}/{quizData.duration_weeks}
            </p>
          </div>
        </div>

        {/* Overall Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="brutalist-card p-4 text-center" data-testid="stat-total">
            <div className="text-sm text-[#4A4A4A] mb-1">Questions Answered</div>
            <div className="text-2xl font-bold">{quizData.overall_total}</div>
          </div>
          <div className="brutalist-card p-4 text-center" data-testid="stat-correct">
            <div className="text-sm text-[#4A4A4A] mb-1">Correct</div>
            <div className="text-2xl font-bold text-green-600">{quizData.overall_correct}/{quizData.overall_total}</div>
          </div>
          <div className="brutalist-card p-4 text-center" data-testid="stat-accuracy">
            <div className="text-sm text-[#4A4A4A] mb-1">Accuracy</div>
            <div className="text-2xl font-bold" style={{ color: quizData.overall_accuracy >= 70 ? "#16a34a" : quizData.overall_accuracy >= 50 ? "#ca8a04" : "#dc2626" }}>
              {quizData.overall_accuracy}%
            </div>
          </div>
        </div>

        {/* Per-Week Results */}
        {quizData.weeks.length === 0 ? (
          <div className="brutalist-card p-12 text-center">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 text-[#4A4A4A]" />
            <p className="text-[#4A4A4A]">This student hasn't completed any quizzes yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {quizData.weeks.map(week => {
              const isExpanded = expandedWeek === week.week_number;
              return (
                <div key={week.week_number} className="brutalist-card overflow-hidden" data-testid={`week-result-${week.week_number}`}>
                  {/* Week Summary Row */}
                  <button
                    onClick={() => setExpandedWeek(isExpanded ? null : week.week_number)}
                    className="w-full p-5 flex items-center gap-4 text-left hover:bg-[#F0EFEB]/50 transition-colors"
                    data-testid={`expand-week-${week.week_number}`}
                  >
                    <div className="w-12 h-12 rounded-full bg-[#D6E6FA] border-2 border-[#0A0A0A] flex items-center justify-center text-lg font-bold shrink-0">
                      {week.week_number}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-lg">Week {week.week_number}</div>
                      <div className="text-sm text-[#4A4A4A]">
                        Completed {formatDate(week.latest_submitted)}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <div className="text-right">
                        <div className="font-bold text-lg">{week.correct}/{week.total}</div>
                        <div className="text-xs text-[#4A4A4A]">correct</div>
                      </div>
                      <div className={`text-lg font-bold px-3 py-1 rounded-full border-2 border-[#0A0A0A] ${
                        week.accuracy >= 70 ? "bg-[#B4F8C8]" : week.accuracy >= 50 ? "bg-[#FBE7C6]" : "bg-[#FFAEBC]"
                      }`}>
                        {week.accuracy}%
                      </div>
                      {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    </div>
                  </button>

                  {/* Expanded Question Details */}
                  {isExpanded && (
                    <div className="border-t-2 border-[#0A0A0A]/10 p-5 bg-[#FDFBF7]" data-testid={`questions-week-${week.week_number}`}>
                      <div className="space-y-3">
                        {week.questions.map((q, idx) => (
                          <div
                            key={idx}
                            className={`p-4 rounded-lg border-2 ${
                              q.is_correct ? "border-green-300 bg-green-50" : "border-red-300 bg-red-50"
                            }`}
                            data-testid={`question-${week.week_number}-${idx}`}
                          >
                            <div className="flex items-start gap-3">
                              {q.is_correct ? (
                                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                              ) : (
                                <XCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm mb-2">{q.question}</div>
                                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                                  <span>
                                    <span className="text-[#4A4A4A]">Student's answer: </span>
                                    <span className={`font-semibold ${q.is_correct ? "text-green-700" : "text-red-600"}`}>
                                      {q.user_answer || "—"}
                                    </span>
                                  </span>
                                  {!q.is_correct && (
                                    <span>
                                      <span className="text-[#4A4A4A]">Correct: </span>
                                      <span className="font-semibold text-green-700">{q.correct_answer}</span>
                                    </span>
                                  )}
                                </div>
                                <div className="flex gap-3 mt-2 text-xs text-[#4A4A4A]">
                                  <span className="px-2 py-0.5 rounded-full bg-white border border-[#0A0A0A]/20 capitalize">{q.difficulty}</span>
                                  <span className="px-2 py-0.5 rounded-full bg-white border border-[#0A0A0A]/20 capitalize">{q.type?.replace("_", " ")}</span>
                                  {q.time_taken_seconds > 0 && (
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-3 h-3" /> {Math.round(q.time_taken_seconds)}s
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Loading detail
  if (loadingQuiz) {
    return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
  }

  // ---- List View: Show all students ----
  return (
    <div data-testid="quiz-results-list">
      <h1 className="text-3xl font-bold mb-2" data-testid="quiz-results-title">Quiz Results</h1>
      <p className="text-[#4A4A4A] mb-8">View homework scores and quiz details for each student</p>

      {students.length === 0 ? (
        <div className="brutalist-card p-12 text-center">
          <Award className="w-12 h-12 mx-auto mb-4 text-[#4A4A4A]" />
          <p className="text-[#4A4A4A] mb-2">No students assigned yet.</p>
          <p className="text-sm text-[#4A4A4A]">Quiz results will appear once students are booked and complete assignments.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {students.map(s => (
            <button
              key={s.enrollment_id}
              onClick={() => loadQuizResults(s.enrollment_id)}
              className="w-full brutalist-card p-5 flex items-center gap-4 text-left hover:bg-[#D6E6FA]/20 transition-colors"
              data-testid={`student-row-${s.enrollment_id}`}
            >
              <div className="w-12 h-12 rounded-full bg-[#A0D2F8] border-2 border-[#0A0A0A] flex items-center justify-center text-lg font-bold shrink-0">
                {s.student_name?.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-lg">{s.student_name}</div>
                <div className="text-sm text-[#4A4A4A]">
                  {s.language_flag} {s.course_name} &middot; Week {s.current_week}/{s.duration_weeks}
                </div>
                {s.scheduled_day_name && s.scheduled_time && (
                  <div className="text-xs text-[#4A4A4A] mt-0.5">
                    {s.scheduled_day_name}s at {formatTime(s.scheduled_time)}
                  </div>
                )}
              </div>
              <ChevronRight className="w-5 h-5 text-[#4A4A4A] shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TutorQuizResults;
